import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  RON_MONITOR_CLOSING_STATES,
  RonMonitorAddressWire,
  RonMonitorBoardWire,
  RonMonitorClosingWire,
  RonMonitorNotaryLaneWire,
  RonMonitorOrderDetailWire,
  RonMonitorOrderParticipantWire,
} from "../models/ron-monitor.api-contract";
import { RonMonitorDataSource } from "../models/ron-monitor.data-source";
import {
  BoardDesignationFilter,
  ClosingDetail,
  ClosingDetailClient,
  ClosingIcon,
  ClosingPill,
  NotaryDesignation,
  NotaryLane,
  RonMonitorBoard,
  RonMonitorSummary,
  SlotVariant,
} from "../models/ron-monitor.models";
import {
  DayKind,
  NO_VALUE,
  dayKindFor,
  isToday,
  laneStatusFor,
  openSlotCount,
} from "../ron-closing-monitor.utils";
import { RonMonitorClock } from "./ron-monitor-clock.service";
import { RonMonitorService } from "./ron-monitor.service";

const STATE_VARIANTS: Record<string, SlotVariant> = {
  [RON_MONITOR_CLOSING_STATES.scheduled]: "scheduled",
  [RON_MONITOR_CLOSING_STATES.checkIn]: "check-in",
  [RON_MONITOR_CLOSING_STATES.preSign]: "pre-sign",
  [RON_MONITOR_CLOSING_STATES.inSession]: "in-session",
  [RON_MONITOR_CLOSING_STATES.processingDocuments]: "processing-documents",
  [RON_MONITOR_CLOSING_STATES.completed]: "completed",
  [RON_MONITOR_CLOSING_STATES.cancelled]: "canceled",
  [RON_MONITOR_CLOSING_STATES.stuck]: "stuck",
  [RON_MONITOR_CLOSING_STATES.borrowerDelayed]: "delayed",
  [RON_MONITOR_CLOSING_STATES.notaryDelayed]: "delayed",
  [RON_MONITOR_CLOSING_STATES.runningOver]: "over",
};

/** Borrower and notary delay share one variant, so the icon is what tells them apart. */
const STATE_ICONS: Record<string, ClosingIcon | null> = {
  [RON_MONITOR_CLOSING_STATES.scheduled]: null,
  [RON_MONITOR_CLOSING_STATES.checkIn]: "person",
  [RON_MONITOR_CLOSING_STATES.preSign]: "stamp",
  [RON_MONITOR_CLOSING_STATES.inSession]: "stamp",
  [RON_MONITOR_CLOSING_STATES.processingDocuments]: null,
  [RON_MONITOR_CLOSING_STATES.completed]: null,
  [RON_MONITOR_CLOSING_STATES.cancelled]: null,
  [RON_MONITOR_CLOSING_STATES.stuck]: "person",
  [RON_MONITOR_CLOSING_STATES.borrowerDelayed]: "person",
  [RON_MONITOR_CLOSING_STATES.notaryDelayed]: "headphone",
  [RON_MONITOR_CLOSING_STATES.runningOver]: "stamp",
};

const CHECKED_IN = "CHECKEDIN";

const PARTICIPANT_STATUS_LABELS = {
  joined: "Joined",
  notJoined: "Not joined",
} as const;

/** Keys are PackageUserStep names plus SigningRoom, as the API sends them. */
const STEP_LABELS: Record<string, string> = {
  WAITINGROOM: "Waiting room",
  CONSENTS: "Consents",
  DEVICESETUP: "Device setup",
  IDENTITYVERIFICATION: "ID verification",
  KNOWLEDGEBASEDAUTH: "Knowledge-based authentication",
  PARTICIPANTREGISTRATION: "Registration",
  SIGNATURESELECTION: "Signature selection",
  PRESIGN: "Pre-sign",
  PRESIGNCOMPLETE: "Pre-sign complete",
  COMMISSIONSELECTION: "Commission selection",
  SIGNINGROOM: "Signing room",
};

/** The single-user link sets CHECKEDIN when the wizard ends and nothing moves the cursor off SignatureSelection until the waiting room; mirrors RonMonitorService in Frontend.Api. */
const LAST_SINGLE_USER_CHECK_IN_STEP = "SIGNATURESELECTION";

const CHECK_IN_COMPLETE_LABEL = "Check-in complete";

interface PostSessionCurrently {
  label: string;
  /** Mirrors LiveClosingStatuses in RonMonitorService (ClearSign Frontend.Api): past those, the API sends no progress, so a missing step no longer means the participant never started. */
  reportsProgress: boolean;
}

interface EndedStatus {
  variant: SlotVariant;
  currently: PostSessionCurrently | null;
}

const SESSION_COMPLETE_LABEL = "Session complete";

const FINALIZING: EndedStatus = {
  variant: "processing-documents",
  currently: { label: SESSION_COMPLETE_LABEL, reportsProgress: true },
};

/** A failed status still reads Complete on purpose: the participants are done either way. */
const COMPLETE: EndedStatus = {
  variant: "completed",
  currently: { label: SESSION_COMPLETE_LABEL, reportsProgress: false },
};

const CANCELLED: EndedStatus = { variant: "canceled", currently: null };

/** Mirrors the ProcessingDocuments, Completed and Cancelled statuses in RonMonitorService.DeriveState (ClearSign Frontend.Api); change the two together. The progress cursor stays on SigningRoom after the session (ClearSign repo ADR/0018), so only the package status says the session ended. */
const ENDED_STATUSES: Record<string, EndedStatus> = {
  SIGNING: FINALIZING,
  SESSIONCOMPLETE: FINALIZING,
  VAULTING: FINALIZING,
  COMPLETE,
  SIGNINGFAILED: COMPLETE,
  VAULTINGFAILED: COMPLETE,
  CANCELLED,
  OPTOUT: CANCELLED,
  EXPIRED: CANCELLED,
};

const ROLE_LABELS: Record<string, string> = {
  BORROWER: "Borrower",
  NBTH: "NBTH",
  SELLER: "Seller",
  WITNESS: "Witness",
  SIGNER: "Signer",
  ATTORNEY: "Attorney",
  SIGNINGAGENT: "Notary",
};

@Injectable()
export class ApiRonMonitorDataSource extends RonMonitorDataSource {
  private readonly ronMonitorService = inject(RonMonitorService);
  private readonly clock = inject(RonMonitorClock);

  getBoard(
    date: Date,
    designation: BoardDesignationFilter
  ): Observable<RonMonitorBoard> {
    return this.ronMonitorService
      .getBoard(date, designation)
      .pipe(map((wire) => this.toBoard(wire, date)));
  }

  getDetail(packageId: number): Observable<ClosingDetail> {
    return this.ronMonitorService
      .getDetail(packageId)
      .pipe(map((wire) => this.toDetail(wire, packageId)));
  }

  private toBoard(wire: RonMonitorBoardWire, date: Date): RonMonitorBoard {
    const now = this.clock.now();
    const closings = (wire?.closings ?? []).map((closing) =>
      this.toPill(closing)
    );
    const dayKind = dayKindFor(date, now);
    const lanes = (wire?.lanes ?? []).map((lane) =>
      this.toLane(lane, closings, dayKind)
    );

    return {
      generatedAt: wire?.generatedAtUtc ?? now.toISOString(),
      summary: this.toSummary(wire, lanes, closings, dayKind, now),
      lanes,
      closings,
    };
  }

  private toSummary(
    wire: RonMonitorBoardWire,
    lanes: NotaryLane[],
    closings: ClosingPill[],
    dayKind: DayKind,
    now: Date
  ): RonMonitorSummary {
    const summary = wire?.summary;

    return {
      scheduled: summary?.scheduled ?? 0,
      stuckClients: summary?.stuckClients ?? 0,
      borrowerDelayed: summary?.borrowerDelayed ?? 0,
      notaryDelayed: summary?.notaryDelayed ?? 0,
      runningOver: summary?.runningOver ?? 0,
      processingDocuments: summary?.processingDocuments ?? 0,
      completed: summary?.completed ?? 0,
      completedScheduledDenominator: summary?.scheduledDenominator ?? 0,
      cancelled: summary?.cancelled ?? 0,
      openSlots: openSlotCount(lanes, closings, isToday(dayKind) ? now : null),
      stuckThresholdMinutes: summary?.stuckThresholdMinutes ?? 0,
    };
  }

  private toPill(closing: RonMonitorClosingWire): ClosingPill {
    return {
      packageId: closing.packageId,
      notaryId:
        closing.notaryAccountId === null ||
        closing.notaryAccountId === undefined
          ? null
          : String(closing.notaryAccountId),
      scheduledDateTime: closing.scheduledDateTime ?? "",
      variant: STATE_VARIANTS[closing.state] ?? "scheduled",
      delayMinutes: closing.delayMinutes ?? null,
      icon: STATE_ICONS[closing.state] ?? null,
      // Empty, not NO_VALUE: search matches on these, and a placeholder would match.
      loanNumber: closing.loanIdentifier ?? "",
      orderNumber: closing.partnerOrderIdentifier ?? "",
    };
  }

  private toLane(
    lane: RonMonitorNotaryLaneWire,
    closings: ClosingPill[],
    dayKind: DayKind
  ): NotaryLane {
    const notaryId = String(lane.notaryAccountId);
    const owned = closings.filter((pill) => pill.notaryId === notaryId);

    return {
      notaryId,
      name: this.personName(lane.firstName, lane.lastName),
      designation: this.toDesignation(lane.designation),
      currentStatus: laneStatusFor(owned, dayKind),
    };
  }

  private toDesignation(designation: string | null): NotaryDesignation | null {
    const normalized = designation?.trim().toUpperCase();

    if (normalized === "TEAMSIGNINGAGENT" || normalized === "TSA") {
      return "TeamSigningAgent";
    }
    if (
      normalized === "PREFERREDAGENT" ||
      normalized === "PA" ||
      normalized === "PPA"
    ) {
      return "PreferredAgent";
    }
    return null;
  }

  /** reason has no data source yet; see feature 294771. */
  private toDetail(
    wire: RonMonitorOrderDetailWire,
    packageId: number
  ): ClosingDetail {
    const closing = wire?.closing;
    const resolvedPackageId = wire?.packageId ?? packageId;
    const statusCode = closing?.statusCode?.trim().toUpperCase() ?? null;
    const ended = statusCode ? ENDED_STATUSES[statusCode] : undefined;
    const postSession = ended?.currently ?? null;

    return {
      packageId: resolvedPackageId,
      endedVariant: ended?.variant ?? null,
      notary: {
        name: wire?.notary
          ? this.personName(wire.notary.firstName, wire.notary.lastName)
          : "Unassigned",
        status: this.participantStatus(wire?.notary?.statusCode ?? null),
        currently: this.currently(wire?.notary ?? null, postSession),
        reason: null,
        phone: wire?.notary?.phoneNumber ?? null,
      },
      clients: (wire?.clients ?? []).map((client) =>
        this.toClient(client, postSession)
      ),
      closing: {
        loanNumber: closing?.loanIdentifier ?? NO_VALUE,
        signingId: String(resolvedPackageId),
        propertyAddress: this.formatAddress(closing?.propertyAddress ?? null),
        scheduledAt: closing?.scheduledDateTime ?? "",
        completedAt: closing?.actualCompleteDateTime ?? null,
        rescheduledTo: null,
      },
    };
  }

  private toClient(
    client: RonMonitorOrderParticipantWire,
    postSession: PostSessionCurrently | null
  ): ClosingDetailClient {
    const role = client.role?.trim().toUpperCase();

    return {
      name: this.personName(client.firstName, client.lastName),
      role: (role && ROLE_LABELS[role]) || client.role || NO_VALUE,
      currently: this.currently(client, postSession),
      phone: client.phoneNumber ?? null,
    };
  }

  /** Null is "not reported" (terminal closing), which is not the same as not joined. */
  private participantStatus(statusCode: string | null): string {
    if (!statusCode) {
      return NO_VALUE;
    }
    return statusCode.trim().toUpperCase() === CHECKED_IN
      ? PARTICIPANT_STATUS_LABELS.joined
      : PARTICIPANT_STATUS_LABELS.notJoined;
  }

  private currently(
    participant: RonMonitorOrderParticipantWire | null,
    postSession: PostSessionCurrently | null
  ): string {
    if (!participant) {
      return NO_VALUE;
    }

    const step = participant.currentStep?.trim();
    if (postSession && (step || !postSession.reportsProgress)) {
      return postSession.label;
    }

    if (!step) {
      return NO_VALUE;
    }

    if (this.hasCompletedCheckIn(participant, step)) {
      return CHECK_IN_COMPLETE_LABEL;
    }

    const label = STEP_LABELS[step.toUpperCase()] ?? step;
    const minutes = participant.minutesInStep;

    return minutes === null || minutes === undefined
      ? label
      : `${label} — ${minutes} min`;
  }

  private hasCompletedCheckIn(
    participant: RonMonitorOrderParticipantWire,
    step: string
  ): boolean {
    return (
      participant.statusCode?.trim().toUpperCase() === CHECKED_IN &&
      step.toUpperCase() === LAST_SINGLE_USER_CHECK_IN_STEP
    );
  }

  /** The board procedure returns NULL names when identity cannot be resolved. */
  private personName(
    firstName: string | null,
    lastName: string | null
  ): string {
    const name = [firstName, lastName]
      .filter((part) => !!part?.trim())
      .join(" ")
      .trim();

    return name || "Unknown";
  }

  private formatAddress(address: RonMonitorAddressWire | null): string {
    if (!address) {
      return NO_VALUE;
    }

    const street = [address.streetAddress1, address.streetAddress2]
      .filter((part) => !!part?.trim())
      .join(" ");
    const zip = [address.zipCode, address.zip4]
      .filter((part) => !!part?.trim())
      .join("-");
    const cityStateZip = [
      address.city,
      [address.stateCode, zip].filter((part) => !!part?.trim()).join(" "),
    ]
      .filter((part) => !!part?.trim())
      .join(", ");

    const formatted = [street, cityStateZip]
      .filter((part) => part.length > 0)
      .join(", ");

    return formatted || NO_VALUE;
  }
}
